/*
 * useSortable: drag a row of a list to a new place in it.
 *
 * Where the row lands comes from where the pointer is, measured against the
 * rows' own positions when the drag began. A row can be let go anywhere over
 * the list, the gaps between rows and past either end included. The browser's
 * own drag and drop only dropped while the pointer was inside the frame of the
 * row being overtaken, which is what made reordering feel broken.
 *
 * Pointer events, so a finger works as well as a mouse. A touch starts a drag
 * only from the row's handle (data-sort-handle, which must not pan), leaving
 * the rest of the row to scroll the page. The rows the dragged one passes
 * slide aside as it goes, so the list always shows the order it will drop in.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// How far the pointer travels before a press on a row becomes a drag, so a
// click on a row stays a click.
const THRESHOLD_PX = 4;

const INTERACTIVE = "button, a, input, select, textarea, label";

/* targetIndex is where a row dragged by dy lands: past every row whose middle
   its own middle has crossed. */
export function targetIndex(rects, from, dy) {
  const r = rects[from];
  const centre = r.top + r.height / 2 + dy;
  let to = from;
  for (let j = from + 1; j < rects.length; j++) {
    if (centre > rects[j].top + rects[j].height / 2) to = j;
  }
  for (let j = from - 1; j >= 0; j--) {
    if (centre < rects[j].top + rects[j].height / 2) to = j;
  }
  return to;
}

/**
 * useSortable(onMove) returns props for the list and for each row. onMove
 * (from, to) is called once, when a row is let go somewhere new.
 */
export default function useSortable(onMove, { disabled = false } = {}) {
  const listRef = useRef(null);
  const [drag, setDrag] = useState(null); // { from, to, dy, slot } while dragging
  const press = useRef(null);
  const moveRef = useRef(onMove);
  moveRef.current = onMove;

  const stop = useRef(() => {});
  useEffect(() => () => stop.current(), []);

  const onPointerDown = useCallback((from) => (e) => {
    if (disabled || (e.button != null && e.button !== 0)) return;
    if (e.target.closest(INTERACTIVE)) return;
    if (e.pointerType === "touch" && !e.target.closest("[data-sort-handle]")) return;
    const rows = listRef.current ? [...listRef.current.querySelectorAll(":scope > [data-sort-row]")] : [];
    if (rows.length < 2) return;
    e.preventDefault(); // no text selection while dragging
    stop.current(); // a second finger replaces the first press
    const rects = rows.map((el) => el.getBoundingClientRect());
    const gap = rects[1].top - rects[0].bottom;
    press.current = { from, startY: e.clientY, rects, slot: rects[from].height + gap, active: false };

    const move = (ev) => {
      const p = press.current;
      if (!p) return;
      const dy = ev.clientY - p.startY;
      if (!p.active && Math.abs(dy) < THRESHOLD_PX) return;
      p.active = true;
      setDrag({ from: p.from, to: targetIndex(p.rects, p.from, dy), dy, slot: p.slot });
    };
    const end = (commit) => (ev) => {
      const p = press.current;
      stop.current();
      if (!p || !p.active) return;
      const to = targetIndex(p.rects, p.from, ev.clientY - p.startY);
      // One render: the rows lose their offsets as the list takes its new order.
      setDrag(null);
      if (commit && to !== p.from) moveRef.current(p.from, to);
    };
    const up = end(true);
    const cancel = end(false);
    const key = (ev) => {
      if (ev.key !== "Escape") return;
      stop.current();
      setDrag(null);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", key);
    stop.current = () => {
      press.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key);
    };
  }, [disabled]);

  /* The offset a row is drawn at mid-drag: the dragged one follows the
     pointer, and the ones it has passed step into the space it left. */
  const offset = (i) => {
    if (!drag) return 0;
    const { from, to, dy, slot } = drag;
    if (i === from) return dy;
    if (from < to && i > from && i <= to) return -slot;
    if (to < from && i >= to && i < from) return slot;
    return 0;
  };

  const rowProps = (i) => ({
    "data-sort-row": "",
    onPointerDown: onPointerDown(i),
    style: drag ? { transform: `translateY(${offset(i)}px)` } : undefined,
  });

  return {
    listRef,
    rowProps,
    dragging: drag ? drag.from : null,
    target: drag ? drag.to : null,
  };
}
